import { Camera, ImagePlus, Loader2, RotateCcw, Trash2, X } from 'lucide-react'
import { useCallback, useRef, useState } from 'react'

import { Button } from '@/components/ui/button'
import { processFileForUpload, isImageFile } from '@/lib/image-utils'
import { cn } from '@/lib/utils'

interface ImageFile {
  id: string
  file: File
  preview: string
  status: 'pending' | 'uploading' | 'success' | 'error'
  progress: number
  error?: string
  retryCount: number
}

interface ImageUploaderProps {
  /** 已上传的图片URL列表 */
  value?: string[]
  /** 图片变更回调 */
  onChange?: (urls: string[]) => void
  /** 上传函数，返回上传后的URL */
  onUpload: (file: File) => Promise<string>
  /** 最大图片数量 */
  maxCount?: number
  /** 最大文件大小（MB），压缩后 */
  maxSizeMB?: number
  /** 是否禁用 */
  disabled?: boolean
  /** 自定义类名 */
  className?: string
}

const MAX_RETRY_COUNT = 3
const WEBP_QUALITY = 0.85

/**
 * 图片上传组件
 * 支持相机拍照、相册选择、自动压缩、上传重试
 * Requirements: 8.2, 8.3, 8.5
 */
export function ImageUploader({
  value = [],
  onChange,
  onUpload,
  maxCount = 5,
  maxSizeMB = 2,
  disabled = false,
  className,
}: ImageUploaderProps) {
  const [files, setFiles] = useState<ImageFile[]>([])
  const fileInputRef = useRef<HTMLInputElement>(null)
  const cameraInputRef = useRef<HTMLInputElement>(null)


  // 处理图片文件（转换为 WebP）
  const processImage = useCallback(
    async (file: File): Promise<{ blob: Blob; fileName: string }> => {
      if (!isImageFile(file)) {
        return { blob: file, fileName: file.name }
      }
      const result = await processFileForUpload(file, WEBP_QUALITY)
      return { blob: result.blob, fileName: result.fileName }
    },
    []
  )

  // 上传单个文件（带重试）
  const uploadFile = useCallback(
    async (imageFile: ImageFile): Promise<string | null> => {
      const updateFile = (updates: Partial<ImageFile>) => {
        setFiles((prev) =>
          prev.map((f) => (f.id === imageFile.id ? { ...f, ...updates } : f))
        )
      }

      updateFile({ status: 'uploading', progress: 0 })

      try {
        // 转换为 WebP
        const { blob, fileName } = await processImage(imageFile.file)
        updateFile({ progress: 30 })

        // 创建新的 File 对象用于上传
        const processedFile = new File([blob], fileName, { type: blob.type })

        // 上传
        const url = await onUpload(processedFile)
        updateFile({ status: 'success', progress: 100 })
        return url
      } catch (error) {
        const newRetryCount = imageFile.retryCount + 1
        if (newRetryCount < MAX_RETRY_COUNT) {
          // 自动重试
          updateFile({ retryCount: newRetryCount, progress: 0 })
          await new Promise((resolve) => setTimeout(resolve, 1000 * newRetryCount))
          return uploadFile({ ...imageFile, retryCount: newRetryCount })
        }
        // 重试次数用尽
        updateFile({
          status: 'error',
          error: error instanceof Error ? error.message : '上传失败',
          progress: 0,
        })
        return null
      }
    },
    [processImage, onUpload]
  )

  // 处理文件选择
  const handleFileSelect = useCallback(
    async (selectedFiles: FileList | null) => {
      if (!selectedFiles || disabled) { return }

      const remainingSlots = maxCount - value.length - files.length
      const filesToAdd = Array.from(selectedFiles).slice(0, remainingSlots)

      if (filesToAdd.length === 0) { return }

      // 创建预览
      const newFiles: ImageFile[] = filesToAdd.map((file) => ({
        id: crypto.randomUUID(),
        file,
        preview: URL.createObjectURL(file),
        status: 'pending' as const,
        progress: 0,
        retryCount: 0,
      }))

      setFiles((prev) => [...prev, ...newFiles])

      // 上传所有文件
      const uploadPromises = newFiles.map(uploadFile)
      const results = await Promise.all(uploadPromises)

      // 更新已上传的URL列表
      const successUrls = results.filter((url): url is string => url !== null)
      if (successUrls.length > 0 && onChange) {
        onChange([...value, ...successUrls])
      }

      // 清理成功上传的文件预览
      setFiles((prev) => prev.filter((f) => f.status !== 'success'))
    },
    [disabled, files.length, maxCount, onChange, uploadFile, value]
  )


  // 手动重试
  const handleRetry = useCallback(
    async (fileId: string) => {
      const file = files.find((f) => f.id === fileId)
      if (!file) { return }

      setFiles((prev) =>
        prev.map((f) => (f.id === fileId ? { ...f, retryCount: 0, status: 'pending' } : f))
      )

      const url = await uploadFile({ ...file, retryCount: 0 })
      if (url && onChange) {
        onChange([...value, url])
        setFiles((prev) => prev.filter((f) => f.id !== fileId))
      }
    },
    [files, onChange, uploadFile, value]
  )

  // 删除文件
  const handleRemove = useCallback(
    (fileId: string) => {
      const file = files.find((f) => f.id === fileId)
      if (file) {
        URL.revokeObjectURL(file.preview)
      }
      setFiles((prev) => prev.filter((f) => f.id !== fileId))
    },
    [files]
  )

  // 删除已上传的图片
  const handleRemoveUploaded = useCallback(
    (index: number) => {
      if (onChange) {
        onChange(value.filter((_, i) => i !== index))
      }
    },
    [onChange, value]
  )

  const canAddMore = value.length + files.length < maxCount

  return (
    <div className={cn('space-y-3', className)}>
      {/* 已上传的图片 */}
      {value.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {value.map((url, index) => (
            <div key={url} className="relative group">
              <img
                src={url}
                alt={`已上传图片 ${index + 1}`}
                className="h-20 w-20 rounded-lg object-cover border"
              />
              {!disabled && (
                <button
                  type="button"
                  onClick={() => handleRemoveUploaded(index)}
                  className="absolute -top-2 -right-2 h-5 w-5 rounded-full bg-destructive text-destructive-foreground flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* 上传中的文件 */}
      {files.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {files.map((file) => (
            <div key={file.id} className="relative">
              <img
                src={file.preview}
                alt="上传中"
                className={cn(
                  'h-20 w-20 rounded-lg object-cover border',
                  file.status === 'error' && 'border-destructive'
                )}
              />
              {/* 上传进度遮罩 */}
              {file.status === 'uploading' && (
                <div className="absolute inset-0 bg-black/50 rounded-lg flex items-center justify-center">
                  <div className="text-white text-xs flex flex-col items-center gap-1">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span>{file.progress}%</span>
                  </div>
                </div>
              )}
              {/* 错误状态 */}
              {file.status === 'error' && (
                <div className="absolute inset-0 bg-black/50 rounded-lg flex items-center justify-center">
                  <div className="flex gap-1">
                    <button
                      type="button"
                      onClick={() => void handleRetry(file.id)}
                      className="h-6 w-6 rounded-full bg-white flex items-center justify-center"
                      title="重试"
                    >
                      <RotateCcw className="h-3 w-3 text-primary" />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleRemove(file.id)}
                      className="h-6 w-6 rounded-full bg-white flex items-center justify-center"
                      title="删除"
                    >
                      <Trash2 className="h-3 w-3 text-destructive" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* 上传按钮 */}
      {canAddMore && !disabled && (
        <div className="flex gap-2">
          {/* 相机拍照 */}
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => cameraInputRef.current?.click()}
            className="flex-1 h-12 sm:h-10"
          >
            <Camera className="h-4 w-4 mr-2" />
            拍照
          </Button>
          {/* 相册选择 */}
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => fileInputRef.current?.click()}
            className="flex-1 h-12 sm:h-10"
          >
            <ImagePlus className="h-4 w-4 mr-2" />
            相册
          </Button>
        </div>
      )}

      {/* 隐藏的文件输入 */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={(e) => void handleFileSelect(e.target.files)}
      />
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={(e) => void handleFileSelect(e.target.files)}
      />

      {/* 提示文字 */}
      <p className="text-xs text-muted-foreground">
        最多上传 {maxCount} 张图片，单张不超过 {maxSizeMB}MB
      </p>
    </div>
  )
}
